import { AnalyticsBrowser } from '@customerio/cdp-analytics-browser';
import * as Sentry from '@sentry/vue';
import AppConfig from '@/AppConfig';
import store from '@/store';

/**
 * Customer.io Data Pipelines SDK.
 *
 * Loaded solely to render in-app messages and to bind the browser session to a
 * person. Behavioural events are NOT sent from here — they go through
 * `EventService` -> checkout-api, where the identity comes from the JWT rather
 * than the browser. Profile attributes are owned by the warehouse sync.
 *
 * Runs alongside Intercom during the migration; `globalAnalyticsKey` keeps it
 * off `window.analytics` so it cannot collide with other Segment-style clients.
 */
const GLOBAL_ANALYTICS_KEY = 'cioAnalytics';

const IN_APP_PLUGIN = 'Customer.io In-App Plugin';

let analytics: AnalyticsBrowser | null = null;

let identified = false;

function getUserId(): string {
  const id = store.state.auth.user.id;
  return id ? String(id) : '';
}

export const customerioBoot = async function (): Promise<void> {
  const writeKey = AppConfig.getCustomerIoWriteKey();
  const siteId = AppConfig.getCustomerIoSiteId();
  const userId = getUserId();

  if (analytics !== null) return;

  // Not provisioned yet, or local dev. The site id is as necessary as the key:
  // without it the SDK loads no in-app plugin, so it would cost a download and
  // render nothing.
  if (writeKey === '' || siteId === '') return;

  // In-app messages target a known person; there is nothing to render for an
  // anonymous visitor, and identifying one would create a junk profile.
  if (userId === '') return;

  analytics = AnalyticsBrowser.load(
    {
      writeKey,
      cdnURL: AppConfig.getCustomerIoCdnUrl() || undefined,
    },
    {
      // The SDK must not emit events of its own — see the note above.
      initialPageview: false,
      globalAnalyticsKey: GLOBAL_ANALYTICS_KEY,
      // Passed here rather than relying on workspace settings, which the SDK
      // reads only if this is absent.
      integrations: { [IN_APP_PLUGIN]: { siteId } },
    }
  );

  try {
    // Identifier only. Traits are written by the warehouse sync, and a second
    // writer here would make profiles flap between batch and real-time values.
    await analytics.identify(userId);
    identified = true;
  } catch (error) {
    Sentry.captureException(error);

    // Dropped so a later boot retries: an unidentified session renders no
    // targeted message, and the guard above would otherwise skip every attempt.
    analytics = null;

    return;
  }

  // The route the user identified on would otherwise go unreported.
  customerioPage();
};

export const customerioPage = function (): void {
  if (analytics === null || !identified) return;

  analytics.page().catch((error) => {
    Sentry.captureException(error);
  });
};

export const customerioShutdown = function (): void {
  if (analytics === null) return;

  analytics.reset();
  analytics = null;
  identified = false;
};
